'use client';

import React, { useState } from 'react';
import { Mail, CheckCircle2 } from 'lucide-react';
import { useLocale } from '@/lib/i18n';

export function NewsletterSection() {
  const { t } = useLocale();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setPending(true);
    setError(null);
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ type: 'newsletter', email, source: 'newsletter-section' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof data.error === 'string' ? data.error : t('news.sub'));
        return;
      }
      setSubscribed(true);
      setTimeout(() => setEmail(''), 3000);
    } catch {
      setError(t('news.sub'));
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-6 sm:p-8 flex flex-col lg:flex-row items-center justify-between gap-6">
        {/* Left: Mail Icon & Text */}
        <div className="flex items-center gap-4 w-full lg:w-auto">
          <div className="w-14 h-14 rounded-2xl border border-amber-500/30 bg-amber-50 flex items-center justify-center shrink-0">
            <Mail className="w-6 h-6 text-[var(--color-primary)]" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-extrabold text-slate-900 tracking-tight">
              {t('news.title')}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {t('news.sub')}
            </p>
          </div>
        </div>

        {/* Right: Email input & Subscribe button */}
        <div className="w-full lg:w-auto lg:min-w-[420px]">
          {subscribed ? (
            <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-4 py-3 rounded-xl border border-emerald-200 text-xs font-bold animate-in fade-in">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{t('news.done')}</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 sm:gap-2">
              <input
                type="email"
                required
                placeholder={t('news.placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="grow px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
              />
              <button
                type="submit"
                disabled={pending}
                className="px-7 py-3 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-95 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-xs hover:shadow-md transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {t('news.cta')}
              </button>
            </form>
          )}
          {error ? <p className="mt-2 text-xs font-semibold text-rose-600">{error}</p> : null}
        </div>
      </div>
    </section>
  );
}
